import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { SEGMENTOS, ESTADOS } from "@/data/pillars";

const REQUIRED = [
  "nome",
  "empresa",
  "cargo",
  "colaboradores",
  "cidade",
  "estado",
  "segmento",
  "email",
];

export default function CompanyInfo() {
  const navigate = useNavigate();
  const { company, setCompany } = useDiagnostic();
  const [errors, setErrors] = useState({});

  const update = (k, v) => setCompany({ ...company, [k]: v });

  const validate = () => {
    const errs = {};
    REQUIRED.forEach((k) => {
      if (!company[k] || !String(company[k]).trim()) {
        errs[k] = "Campo obrigatório";
      }
    });
    if (
      company.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(company.email).trim())
    ) {
      errs.email = "E-mail inválido";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (validate()) navigate("/diagnostico");
  };

  return (
    <div data-testid="company-info-page" className="max-w-3xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="hub-eyebrow">Etapa 1 de 3 · Contexto da empresa</div>
      <h1 className="mt-3 font-display font-medium text-3xl sm:text-4xl tracking-tight leading-tight">
        Antes de começarmos, conte-nos sobre a sua empresa.
      </h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        Essas informações personalizam o seu relatório executivo e serão
        utilizadas apenas para a geração do diagnóstico.
      </p>

      <form onSubmit={submit} className="mt-12 hub-card p-8 sm:p-10">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Nome completo" testid="field-nome" error={errors.nome}>
            <input
              data-testid="input-nome"
              className="hub-input"
              value={company.nome}
              onChange={(e) => update("nome", e.target.value)}
              placeholder="Ex.: Ana Souza"
            />
          </Field>
          <Field label="Empresa" testid="field-empresa" error={errors.empresa}>
            <input
              data-testid="input-empresa"
              className="hub-input"
              value={company.empresa}
              onChange={(e) => update("empresa", e.target.value)}
              placeholder="Razão social ou nome fantasia"
            />
          </Field>
          <Field label="Cargo" testid="field-cargo" error={errors.cargo}>
            <input
              data-testid="input-cargo"
              className="hub-input"
              value={company.cargo}
              onChange={(e) => update("cargo", e.target.value)}
              placeholder="Ex.: Diretora de Operações"
            />
          </Field>
          <Field
            label="Quantidade de colaboradores"
            testid="field-colaboradores"
            error={errors.colaboradores}
          >
            <select
              data-testid="input-colaboradores"
              className="hub-input"
              value={company.colaboradores}
              onChange={(e) => update("colaboradores", e.target.value)}
            >
              <option value="">Selecione…</option>
              <option>1 a 9</option>
              <option>10 a 49</option>
              <option>50 a 99</option>
              <option>100 a 249</option>
              <option>250 a 499</option>
              <option>500 a 999</option>
              <option>1000+</option>
            </select>
          </Field>
          <Field label="Cidade" testid="field-cidade" error={errors.cidade}>
            <input
              data-testid="input-cidade"
              className="hub-input"
              value={company.cidade}
              onChange={(e) => update("cidade", e.target.value)}
              placeholder="Ex.: Curitiba"
            />
          </Field>
          <Field label="Estado (UF)" testid="field-estado" error={errors.estado}>
            <select
              data-testid="input-estado"
              className="hub-input"
              value={company.estado}
              onChange={(e) => update("estado", e.target.value)}
            >
              <option value="">Selecione…</option>
              {ESTADOS.map((uf) => (
                <option key={uf}>{uf}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Segmento da empresa"
            testid="field-segmento"
            error={errors.segmento}
            full
          >
            <select
              data-testid="input-segmento"
              className="hub-input"
              value={company.segmento}
              onChange={(e) => update("segmento", e.target.value)}
            >
              <option value="">Selecione…</option>
              {SEGMENTOS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="E-mail corporativo" testid="field-email" error={errors.email}>
            <input
              data-testid="input-email"
              className="hub-input"
              type="email"
              value={company.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="voce@empresa.com"
            />
          </Field>
          <Field label="Telefone (opcional)" testid="field-telefone">
            <input
              data-testid="input-telefone"
              className="hub-input"
              value={company.telefone}
              onChange={(e) => update("telefone", e.target.value)}
              placeholder="(11) 90000-0000"
            />
          </Field>
        </div>

        <div className="mt-10 flex items-center justify-between gap-4 pt-8 border-t border-slate-200">
          <button
            type="button"
            data-testid="btn-back"
            onClick={() => navigate("/")}
            className="hub-btn-secondary"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <button
            type="submit"
            data-testid="btn-continue"
            className="hub-btn-primary"
          >
            Continuar <ArrowRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, error, testid, full }) {
  return (
    <label
      data-testid={testid}
      className={`block ${full ? "sm:col-span-2" : ""}`}
    >
      <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
        {label}
      </span>
      {children}
      {error && (
        <span className="block mt-1.5 text-xs text-[#DC2626]">{error}</span>
      )}
    </label>
  );
}
